import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Zap, Shield, BarChart3, Cpu, Layers, ArrowRight, Check, PieChart, AlertTriangle,
} from "lucide-react";
import { LangToggle } from "../lib/publicLang";

function TrustBadgeEn() {
  const [m, setM] = useState(null);
  useEffect(() => {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/reporting/trust-badge`)
      .then((r) => r.json()).then(setM).catch(() => {});
  }, []);
  if (!m) return null;
  const pct = (v) => (v == null ? "N/A" : `${v}%`);
  const Metric = ({ label, value }) => (
    <div className="flex items-center gap-1.5">
      <span className="text-white/40 uppercase tracking-widest text-[9px]">{label}</span>
      <span className="font-mono font-semibold text-white">{value}</span>
    </div>
  );
  return (
    <div className="mt-8 inline-flex flex-wrap items-center gap-x-5 gap-y-2 rounded-2xl border border-up/30 bg-up/5 px-5 py-3 text-xs backdrop-blur" data-testid="trust-badge-en">
      <div className="flex items-center gap-2 pr-3 border-r border-white/10">
        <span className="w-2 h-2 rounded-full bg-up animate-pulse" />
        <span className="uppercase tracking-widest text-[10px] font-semibold text-up">Methodology demo</span>
      </div>
      <Metric label="Sharpe" value={m.avg_sharpe} />
      <Metric label="Profit Factor" value={m.avg_profit_factor} />
      <Metric label="DSR (real edge)" value={pct(m.dsr_prob_pct)} />
      <Metric label="PBO (overfit)" value={pct(m.pbo_pct)} />
      <div className="w-full pt-1.5 mt-0.5 border-t border-white/10 text-[10px] text-yellow-400/90">
        📊 Methodology demo on a sample dataset — your reports will be computed on your own imported trades
      </div>
    </div>
  );
}

export default function LandingEn({ lang, setLang }) {
  return (
    <div className="min-h-screen bg-bg text-white" data-testid="landing-page-en">
      <div className="bg-red-950/60 border-b border-red-500/30 text-red-200 text-[11px] px-6 py-2 text-center" data-testid="risk-top-banner-en">
        <span className="font-semibold">⚠️ WARNING</span> · ANATRADEX is a <b>statistical analysis tool</b> and does NOT provide investment advice. Trading involves substantial risk of loss — 70-85% of retail traders lose money (ESMA). ANATRADEX is not a regulated investment firm and is not registered with any financial authority.
      </div>
      <nav className="border-b border-white/10 sticky top-0 backdrop-blur-lg bg-bg/80 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex justify-between items-center">
          <div className="flex items-center gap-2">
            <img src="/favicon.svg" alt="ANATRADEX logo" className="w-9 h-9 rounded-lg" width="36" height="36" />
            <div>
              <div className="font-heading font-bold text-lg leading-none">ANATRADEX</div>
              <div className="text-[10px] text-white/50 uppercase tracking-widest hidden sm:block">Statistical analysis tool</div>
            </div>
          </div>
          <div className="flex items-center gap-2.5 sm:gap-4">
            <Link to="/methodology" className="text-sm text-white/70 hover:text-white transition hidden lg:inline">Methodology</Link>
            <Link to="/plateformes" className="text-sm text-white/70 hover:text-white transition hidden lg:inline">Platforms</Link>
            <Link to="/pricing" className="text-sm text-white/70 hover:text-white transition hidden sm:inline">Pricing</Link>
            <Link to="/beta" className="text-sm text-warn hover:text-white transition hidden lg:inline" data-testid="landing-beta-link-en">Beta (invite-only)</Link>
            <Link to="/login" className="text-sm text-white/70 hover:text-white transition hidden sm:inline">Log in</Link>
            <Link to="/signup" className="btn btn-primary whitespace-nowrap" data-testid="landing-cta-signup-en"><span className="sm:hidden">Free trial</span><span className="hidden sm:inline">Free 14-day trial</span> <ArrowRight size={14} /></Link>
            <LangToggle lang={lang} setLang={setLang} />
          </div>
        </div>
      </nav>

      <section className="max-w-7xl mx-auto px-6 py-24 relative">
        <div className="absolute inset-0 bg-gradient-to-b from-accent-blue/10 via-transparent to-transparent pointer-events-none" />
        <div className="relative">
          <div className="flex flex-wrap items-center gap-3 mb-6">
            <div className="inline-flex items-center gap-2 px-3 py-1 bg-accent-blue/10 border border-accent-blue/30 rounded-full text-xs uppercase tracking-widest text-accent-blue">
              <div className="w-2 h-2 rounded-full bg-accent-blue animate-pulse" />
              For serious prop traders, independent traders & quant devs
            </div>
            <button
              onClick={() => setLang("fr")}
              className="inline-flex items-center gap-1.5 px-3 py-1 bg-white/5 border border-white/15 rounded-full text-xs text-white/70 hover:text-white hover:border-white/35 transition"
              data-testid="landing-fr-badge"
            >
              🇫🇷 Interface disponible en français
            </button>
          </div>
          <h1 className="font-heading font-bold text-5xl md:text-6xl lg:text-7xl leading-[1.05] tracking-tight max-w-4xl">
            The analysis tool<br />
            <span className="bg-gradient-to-r from-accent-blue to-cyan-300 bg-clip-text text-transparent">serious trading finally deserves.</span>
          </h1>
          <p className="mt-8 text-lg text-white/60 max-w-2xl leading-relaxed">
            Statistical analysis and robustness testing (PBO, Deflated Sharpe) on <b className="text-white">indices, stocks, crypto, commodities and forex</b>.
            A self-service tool to <b className="text-white">test your own strategies</b> — from <span className="italic">&ldquo;it works on my screen&rdquo;</span> to <span className="italic">&ldquo;statistically significant&rdquo;</span>.
          </p>
          <div className="mt-10 flex flex-wrap items-center gap-4">
            <Link to="/signup" className="btn btn-primary text-base px-6 py-3" data-testid="hero-cta-signup-en">Start free 14-day trial <ArrowRight size={16} /></Link>
            <Link to="/app" className="text-sm text-white/60 hover:text-white ml-2">Try the demo (no account) →</Link>
          </div>
          <div className="mt-12 flex flex-wrap gap-8 text-sm text-white/60">
            <div className="flex items-center gap-2"><Check size={14} className="text-up" /> No credit card required</div>
            <div className="flex items-center gap-2"><Check size={14} className="text-up" /> Cancel in 1 click</div>
            <div className="flex items-center gap-2"><Check size={14} className="text-up" /> Every metric reproducible (public seed)</div>
          </div>
          <TrustBadgeEn />
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 py-24 border-t border-white/10">
        <div className="mb-16">
          <div className="text-xs uppercase tracking-widest text-accent-blue mb-4">What sets us apart</div>
          <h2 className="font-heading font-bold text-4xl md:text-5xl tracking-tight max-w-3xl">The statistical rigor missing from 99% of retail platforms.</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[
            { icon: Zap, title: "Realistic paper execution", desc: "Slippage, spread, latency, market impact — modelled like a real limit order book." },
            { icon: Layers, title: "Level-2 Order Flow", desc: "Volume Profile (POC/VAH/VAL), OFI (Cont-Kukanov-Stoikov), VWAP z-score, absorption events." },
            { icon: BarChart3, title: "Event-driven backtesting", desc: "Real network + exchange latency, queue position, causal ordering — not a naive OHLC loop." },
            { icon: Cpu, title: "Trained & validated ML", desc: "Purged K-Fold CV, Deflated Sharpe, permutation tests, White's Reality Check." },
            { icon: Shield, title: "Risk controls", desc: "Pre-trade engine, kill-switch that flattens positions, demo data exports (CSV/JSON)." },
            { icon: PieChart, title: "Regime-switching allocation", desc: "Fractional Kelly, correlation-adjusted, ADX + Hurst regime detection." },
          ].map((f, i) => (
            <div key={i} className="panel p-6 hover:border-accent-blue/50 transition-colors group">
              <f.icon className="text-accent-blue mb-4 group-hover:scale-110 transition-transform" size={28} />
              <div className="font-heading font-semibold text-lg mb-2">{f.title}</div>
              <div className="text-sm text-white/60 leading-relaxed">{f.desc}</div>
            </div>
          ))}
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 py-24 border-t border-white/10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <div className="text-xs uppercase tracking-widest text-accent-blue mb-4">Statistical significance</div>
            <h2 className="font-heading font-bold text-4xl tracking-tight">If your edge isn&apos;t statistically proven, it&apos;s not an edge.</h2>
            <p className="mt-6 text-white/60 leading-relaxed">
              Most retail backtests show Sharpe {'>'} 3 driven by massive overfitting. We test every strategy
              with the same statistical standards as published quantitative research: bootstrap CIs, permutation
              tests, Deflated Sharpe (Bailey-López de Prado), and White&apos;s Reality Check.
            </p>
            <ul className="mt-8 space-y-3 text-sm">
              {[
                "Deflated Sharpe correcting for multiple-testing bias",
                "Purged K-Fold CV to eliminate look-ahead bias",
                "1000+ bootstrap resamples for real confidence intervals",
                "Sign-flip permutation tests against H₀ = zero alpha (deterministic seed, reproducible to the digit)",
              ].map((s, i) => (
                <li key={i} className="flex items-start gap-3">
                  <Check size={16} className="text-up mt-0.5 flex-shrink-0" />
                  <span className="text-white/80">{s}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="panel p-8 font-mono text-xs">
            <div className="text-white/40 mb-3">// Live significance report</div>
            <div className="space-y-2 text-white/80">
              <div>Observed Sharpe: <span className="text-accent-blue">2.14</span></div>
              <div>Bootstrap 95% CI: <span className="text-accent-blue">[1.42, 2.87]</span></div>
              <div>Deflated Sharpe P: <span className="text-up">0.983</span> ✓ significant at 5%</div>
              <div>Permutation p-value: <span className="text-up">0.008</span> ✓ significant at 1%</div>
              <div className="pt-3 border-t border-white/10 text-white/60">→ Edge likely REAL (not overfit)</div>
            </div>
          </div>
        </div>
      </section>

      <section className="border-t border-white/10 bg-gradient-to-b from-white/[0.02] to-transparent">
        <div className="max-w-7xl mx-auto px-6 py-24">
          <h2 className="font-heading font-bold text-4xl md:text-5xl tracking-tight leading-tight">
            The statistical report<br /><span className="text-up">that proves your rigor.</span>
          </h2>
          <p className="mt-6 text-lg text-white/70 max-w-2xl">
            Auto-generated 4-page PDF: OOS Sharpe, Probability of Backtest Overfitting (PBO), Deflated
            Sharpe, permutation p-values, data provenance, SHA-256 integrity attestation. Every number
            reproducible — seed printed in the report, raw data exportable.
          </p>
          <div className="mt-10 flex flex-wrap gap-4">
            <a href={`${process.env.REACT_APP_BACKEND_URL}/api/reporting/dd-pack?use_real_trades=false`}
               target="_blank" rel="noopener noreferrer" className="btn btn-primary text-base px-8 py-4"
               data-testid="dd-pack-download-btn-en"
               onClick={(e) => {
                 if (!localStorage.getItem("anatradex-report-ack")) {
                   e.preventDefault();
                   const ok = window.confirm(
                     "IMPORTANT NOTICE\n\nThis is a DEMO REPORT: it illustrates the methodology and format on a sample dataset — not real performance.\n\nIt is NOT investment advice, a strategy certification, or a promise of future performance.\n\n70-85% of retail traders lose money (ESMA). ANATRADEX is not a regulated investment firm.\n\nClick OK if you understand this report is an analysis tool, not a guarantee.");
                   if (ok) { localStorage.setItem("anatradex-report-ack", "1"); window.open(e.currentTarget.href, "_blank"); }
                 }
               }}>
              📥 Download a sample (PDF)
            </a>
            <Link to="/auth" className="btn text-base px-8 py-4">Generate with my trades →</Link>
            <a href={`${process.env.REACT_APP_BACKEND_URL}/api/reporting/dd-pack-data?use_real_trades=false&format=csv`}
               target="_blank" rel="noopener noreferrer" className="btn text-base px-8 py-4">⬇ Raw data (CSV)</a>
          </div>
          <p className="mt-4 text-xs text-white/40 max-w-2xl">
            The sample is a <b>demo report</b> computed on a seeded example dataset. Every figure is verifiable
            via the raw-data export. Create an account to generate a report from your own trades (MT4/MT5/cTrader/CSV import, no code required).
          </p>
        </div>
      </section>

      <footer className="border-t border-white/10 py-12">
        <div className="max-w-7xl mx-auto px-6">
          <div className="mb-10 rounded-xl border-2 border-warn/40 bg-warn/5 p-5" data-testid="landing-risk-warning-en">
            <div className="flex items-start gap-3">
              <AlertTriangle className="text-warn shrink-0 mt-0.5" size={20} />
              <div className="text-xs text-white/70 leading-relaxed">
                <b className="text-warn uppercase tracking-widest">Risk warning</b> — Trading financial markets
                (forex, indices, crypto-assets, commodities, stocks) carries a <b>substantial risk of loss, up to 100% of invested capital</b>, or more with leverage. According to European regulators (ESMA), <b>70-85% of retail traders lose money</b>. ANATRADEX is a self-service <b>analysis and learning tool</b>, not an investment advisory service. ANATRADEX is <b>not</b> a regulated investment firm and is not registered with any financial authority (including the AMF, SEC/FINRA or FCA). Displayed metrics (Sharpe, PBO, DSR) are computed on backtest and demo data — <b>they do not guarantee future performance</b>. Only trade capital you can afford to lose.
              </div>
            </div>
          </div>
          <div className="pt-6 border-t border-white/10 flex flex-col md:flex-row justify-between items-center gap-4 text-xs text-white/40">
            <div>© 2026 ANATRADEX — Statistical analysis tool for traders. Not an investment adviser.</div>
            <div className="flex gap-4">
              <Link to="/risk-disclosure" className="hover:text-white">Risk disclosure</Link>
              <Link to="/terms" className="hover:text-white">Terms</Link>
              <Link to="/privacy" className="hover:text-white">Privacy</Link>
              <a href="mailto:contact@anatradex.com" className="hover:text-white">contact@anatradex.com</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
